import IAppointment from './IAppointment';

// ============================================================================
// CALENDAR SETTINGS - Sistema de configuración escalable
// ============================================================================

/**
 * Configuración de vista del calendario
 */
export interface ICalendarViewSettings {
  /** Mostrar domingos en la vista semanal */
  showSundays: boolean;
  /** Mostrar día completo (0:00-24:00) en vez de jornada laboral */
  showFullDay: boolean;
  /** Hora de inicio del calendario (0-23) - usado si showFullDay es false */
  dayStartHour: number;
  /** Hora de fin del calendario (0-23) - usado si showFullDay es false */
  dayEndHour: number;
  /** Mostrar línea de hora actual */
  showCurrentTimeIndicator: boolean;
  /** Atenuar citas pasadas */
  dimPastAppointments: boolean;
}

/**
 * Modo de color para las citas
 */
export type ColorMode = 'treatment' | 'status';

/**
 * Configuración visual de las citas
 */
export interface ICalendarDisplaySettings {
  /** Modo compacto para tarjetas de citas */
  compactCards: boolean;
  /** Mostrar duración de la cita */
  showDuration: boolean;
  /** Mostrar iconos de estado */
  showStatusIcons: boolean;
  /** Modo de color: por tratamiento o por estado */
  colorMode: ColorMode;
}

/**
 * Tipo de filtro de visibilidad
 */
export type FilterType = 'show' | 'hide';

/**
 * Definición de un filtro de visibilidad
 */
export interface IVisibilityFilter {
  id: string;
  labelKey: string;
  icon: string;
  type: FilterType;
  active: boolean;
  matchFn: (appointment: IAppointment) => boolean;
}

/**
 * Configuración completa del calendario
 */
export interface ICalendarSettings {
  view: ICalendarViewSettings;
  display: ICalendarDisplaySettings;
  filters: IVisibilityFilter[];
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Verifica si una cita está pagada
 */
function isAppointmentPaid(apt: IAppointment): boolean {
  if (!apt.treatments || apt.treatments.length === 0) return true;
  let paid = 0;
  let cost = 0;
  apt.treatments.forEach((treatment) => {
    cost += treatment.price || 0;
    if (treatment.payments) {
      treatment.payments.forEach((payment) => {
        paid += payment.amount || 0;
      });
    }
  });
  return paid >= cost;
}

/**
 * Verifica si los datos del paciente están completos
 */
function isPatientDataComplete(apt: IAppointment): boolean {
  if (!apt.patient) return true;
  return ['name', 'surname', 'email', 'phone', 'address'].every(
    (field) => !!apt.patient[field],
  );
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/**
 * Valores por defecto para la configuración de vista
 */
export const DEFAULT_VIEW_SETTINGS: ICalendarViewSettings = {
  showSundays: false,
  showFullDay: false,
  dayStartHour: 8,
  dayEndHour: 21,
  showCurrentTimeIndicator: true,
  dimPastAppointments: false,
};

/**
 * Valores por defecto para la configuración visual
 */
export const DEFAULT_DISPLAY_SETTINGS: ICalendarDisplaySettings = {
  compactCards: false,
  showDuration: false,
  showStatusIcons: true,
  colorMode: 'treatment',
};

/**
 * Definición de filtros de visibilidad disponibles
 */
export const VISIBILITY_FILTERS: Omit<IVisibilityFilter, 'active'>[] = [
  {
    id: 'unpaid',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.UNPAID',
    icon: 'fas fa-euro-sign',
    type: 'show',
    matchFn: (apt) => !isAppointmentPaid(apt),
  },
  {
    id: 'no_treatment',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.NO_TREATMENT',
    icon: 'fas fa-tooth',
    type: 'show',
    matchFn: (apt) => !apt.treatments || apt.treatments.length === 0,
  },
  {
    id: 'no_patient',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.NO_PATIENT',
    icon: 'fas fa-user-slash',
    type: 'show',
    matchFn: (apt) => !apt.patient || !apt.patient.id || apt.patient.id === 0,
  },
  {
    id: 'patient_incomplete',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.PATIENT_INCOMPLETE',
    icon: 'fas fa-user-edit',
    type: 'show',
    matchFn: (apt) => {
      if (!apt.patient || !apt.patient.id) return false;
      return !isPatientDataComplete(apt);
    },
  },
  {
    id: 'with_issues',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.WITH_ISSUES',
    icon: 'fas fa-exclamation-triangle',
    type: 'show',
    matchFn: (apt) => {
      const noPatient = !apt.patient || !apt.patient.id || apt.patient.id === 0;
      const noTreatment = !apt.treatments || apt.treatments.length === 0;
      const notPaid = !isAppointmentPaid(apt);
      return noPatient || noTreatment || notPaid;
    },
  },
  {
    id: 'canceled',
    labelKey: 'CALENDAR.SETTINGS.FILTERS.CANCELED',
    icon: 'fas fa-ban',
    type: 'hide',
    matchFn: (apt) => apt.status?.id === 3,
  },
];

/**
 * Inicializa los filtros con su estado por defecto
 */
export function initializeFilters(): IVisibilityFilter[] {
  return VISIBILITY_FILTERS.map((filter) => ({
    ...filter,
    active: false,
  }));
}

/**
 * Crea la configuración completa con valores por defecto
 */
export function createDefaultSettings(): ICalendarSettings {
  return {
    view: { ...DEFAULT_VIEW_SETTINGS },
    display: { ...DEFAULT_DISPLAY_SETTINGS },
    filters: initializeFilters(),
  };
}

/**
 * Aplica los filtros activos a una lista de citas
 */
export function applyFilters(
  appointments: IAppointment[],
  filters: IVisibilityFilter[],
): IAppointment[] {
  const showFilters = filters.filter((f) => f.type === 'show' && f.active);
  const hideFilters = filters.filter((f) => f.type === 'hide');

  return appointments.filter((apt) => {
    // Si hay filtros 'show' activos, la cita debe cumplir AL MENOS UNO
    if (showFilters.length > 0) {
      const matchesAnyShowFilter = showFilters.some((f) => f.matchFn(apt));
      if (!matchesAnyShowFilter) return false;
    }

    // Para filtros 'hide': si está inactivo, se ocultan las que coinciden
    for (const filter of hideFilters) {
      if (filter.matchFn(apt) && !filter.active) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Cuenta los filtros 'show' activos
 */
export function countActiveShowFilters(filters: IVisibilityFilter[]): number {
  return filters.filter((f) => f.type === 'show' && f.active).length;
}
